import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  Users, Calendar, Mail, Phone, Trash2, CheckCircle, Clock, 
  XCircle, LogOut, Home, Edit, FileText
} from 'lucide-react';
import ContentEditor from '../components/ContentEditor';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800'
};

export const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    if (loggedIn) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/bookings`)
      ]);
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const loginData = {
        username: credentials.username.trim(),
        password: credentials.password
      };
      console.log('Attempting login with:', { username: loginData.username });
      const response = await axios.post(`${API}/admin/login`, loginData);
      console.log('Login response:', response.data);
      setIsLoggedIn(true);
      sessionStorage.setItem('adminLoggedIn', 'true');
      fetchData();
      toast.success('Login successful');
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminLoggedIn');
    toast.info('Logged out');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`);
      fetchData();
      toast.success(`Booking ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/contact/${contactId}`);
      fetchData();
      toast.success('Contact deleted');
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      fetchData();
      toast.success('Booking deleted');
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#1a1412] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="bg-[#d4cbbe] p-8 rounded-xl shadow-2xl max-w-md w-full" data-testid="admin-login-form">
          <h1 className="font-heading text-3xl font-bold text-[#1a1412] text-center mb-2">
            Admin Login
          </h1>
          <p className="font-body text-[#5a4a3a] text-center mb-8">
            D Brooks Law Dashboard
          </p>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="font-body text-xs uppercase tracking-widest text-[#5a4a3a] mb-2 block">
                Username
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-4 py-3 border border-[#b4a494] rounded-lg bg-white/80 focus:ring-2 focus:ring-[#C9A86C] focus:border-transparent"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="font-body text-xs uppercase tracking-widest text-[#5a4a3a] mb-2 block">
                Password
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-4 py-3 border border-[#b4a494] rounded-lg bg-white/80 focus:ring-2 focus:ring-[#C9A86C] focus:border-transparent"
                required
                data-testid="admin-password-input"
              />
            </div>
            <button type="submit" className="w-full py-3 bg-[#C9A86C] hover:bg-[#b8975b] text-white font-medium rounded-lg transition-colors" data-testid="admin-login-btn">
              Login
            </button>
          </form>
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 py-3 border border-[#5a4a3a] text-[#5a4a3a] hover:bg-[#5a4a3a] hover:text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="admin-back-btn"
          >
            <Home size={16} /> Back to Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-[#1a1412] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">D Brooks Law</h1>
            <p className="text-xs text-[#C9A86C] tracking-widest uppercase">Admin Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="admin-view-site-btn"
            >
              <Home size={18} /> View Site
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Calendar className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="font-heading text-2xl font-bold">{bookings.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="text-yellow-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="font-heading text-2xl font-bold">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Confirmed</p>
                <p className="font-heading text-2xl font-bold">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Contacts</p>
                <p className="font-heading text-2xl font-bold">{contacts.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'bookings' ? 'bg-[#1a1412] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="admin-tab-bookings"
          >
            <Calendar size={18} />
            Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'contacts' ? 'bg-[#1a1412] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="admin-tab-contacts"
          >
            <Mail size={18} />
            Contacts ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'content' ? 'bg-[#1a1412] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="admin-tab-content"
          >
            <FileText size={18} />
            Edit Pages
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-[#C9A86C] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : activeTab === 'content' ? (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <ContentEditor />
          </div>
        ) : activeTab === 'bookings' ? (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-bookings-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Client</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Service</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Date/Time</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-500">
                        No bookings yet
                      </td>
                    </tr>
                  ) : (
                    bookings.map((booking) => (
                      <tr key={booking.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <p className="font-medium text-[#1a1412]">{booking.name}</p>
                          <p className="text-xs text-gray-600">{booking.email}</p>
                          <p className="text-xs text-gray-600">{booking.phone}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-gray-800">{booking.consultation_type}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm">{booking.preferred_date}</p>
                          <p className="text-sm text-gray-600">{booking.preferred_time}</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs uppercase ${statusColors[booking.status]}`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {booking.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded"
                                  title="Confirm"
                                >
                                  <CheckCircle size={18} />
                                </button>
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                                  title="Cancel"
                                >
                                  <XCircle size={18} />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => deleteBooking(booking.id)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-contacts-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Name</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Contact</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Message</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Date</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-500">
                        No contacts yet
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr key={contact.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <p className="font-medium text-[#1a1412]">{contact.name}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <Mail size={14} /> {contact.email}
                          </p>
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <Phone size={14} /> {contact.phone}
                          </p>
                        </td>
                        <td className="p-4 max-w-xs">
                          <p className="text-sm text-gray-700 truncate">{contact.message}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-xs text-gray-600">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => deleteContact(contact.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
